import * as THREE from 'three'
import { nameMatchesArticle } from '@/utils/glbMeshMatching'

export type BaseWorld = (mesh: THREE.Mesh) => THREE.Matrix4

export function occurrenceNode(
  mesh: THREE.Mesh,
  articleSet: Set<string>,
  known: Set<string>,
): THREE.Object3D | null {
  const nodes: THREE.Object3D[] = []
  let cur: THREE.Object3D | null = mesh
  while (cur) {
    nodes.push(cur)
    cur = cur.parent
  }
  let idx = nodes.findIndex((n) => !!n.name && nameMatchesArticle(n.name, articleSet, false, known))
  if (idx < 0) return null
  if (idx === 0 && nodes[1]?.name && nameMatchesArticle(nodes[1].name, articleSet, false, known)) {
    idx = 1
  }
  return nodes[idx]
}

export function baseBoxOf(meshes: THREE.Mesh[], baseWorld: BaseWorld): THREE.Box3 {
  const box = new THREE.Box3()
  for (const m of meshes) {
    const geometry = m.geometry as THREE.BufferGeometry
    if (!geometry.boundingBox) geometry.computeBoundingBox()
    if (geometry.boundingBox) box.union(geometry.boundingBox.clone().applyMatrix4(baseWorld(m)))
  }
  return box
}

export function sortOccurrencesByPosition(
  occurrences: THREE.Mesh[][],
  baseWorld: BaseWorld,
): THREE.Mesh[][] {
  return occurrences
    .map((meshes) => {
      const box = baseBoxOf(meshes, baseWorld)
      return {
        meshes,
        centroid: box.isEmpty() ? new THREE.Vector3() : box.getCenter(new THREE.Vector3()),
      }
    })
    .sort(
      (a, b) =>
        a.centroid.x - b.centroid.x || a.centroid.y - b.centroid.y || a.centroid.z - b.centroid.z,
    )
    .map(({ meshes }) => meshes)
}

export function groupOccurrences(
  meshes: THREE.Mesh[],
  articleSet: Set<string>,
  known: Set<string>,
  baseWorld: BaseWorld,
): THREE.Mesh[][] {
  const byOccurrence = new Map<string, THREE.Mesh[]>()
  for (const mesh of meshes) {
    const id = occurrenceNode(mesh, articleSet, known)?.uuid ?? `mesh:${mesh.uuid}`
    const group = byOccurrence.get(id)
    if (group) group.push(mesh)
    else byOccurrence.set(id, [mesh])
  }
  return sortOccurrencesByPosition([...byOccurrence.values()], baseWorld)
}
